import React, { useState } from "react";
import { Card, CardBody, CardHeader, Badge } from "reactstrap";
import { useTranslation } from "context/TranslationProvider";
import { useNotification } from "providers/NotificationProvider";
import userServices from "services/userServices";
import helpers from "utils/helpers";

const DetailRow = ({ label, value, mono = false, danger = false }) => (
  <div className="d-flex justify-content-between align-items-start py-2 border-bottom flex-wrap pd-gap-2">
    <span className="text-xs text-slate-400 font-weight-bold text-uppercase ls-1 mr-3" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
      {label}
    </span>
    <span className={`text-13 text-right break-all ${mono ? "text-monospace" : ""} ${danger ? "text-danger" : "text-dark"}`} style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
      {value}
    </span>
  </div>
);

const CopyableRow = ({ label, value }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="d-flex justify-content-between align-items-center py-2 border-bottom flex-wrap pd-gap-2">
      <span className="text-xs text-slate-400 font-weight-bold text-uppercase ls-1 mr-3" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
        {label}
      </span>
      <div className="d-flex align-items-center pd-gap-2 ml-auto">
        <span className="text-xs text-monospace text-dark break-all text-right" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
          {value}
        </span>
        <button
          onClick={handleCopy}
          title={copied ? "Copied!" : "Copy"}
          className={`btn-icon-only btn-sm rounded pd-copy-btn ${copied ? "pd-copy-btn--copied" : "pd-copy-btn--normal"}`}
        >
          <i
            className={`ni ${copied ? "ni-check-bold" : "ni-single-copy-04"} text-xs`}
            style={{ color: copied ? "#15803d" : "#94a3b8" }}
          />
        </button>
      </div>
    </div>
  );
};

const getStatusNote = (status, t) => {
  const map = {
    unpaid:       t("page.orders.noPaymentYet", "Payment has not been initiated yet."),
    pending_card: t("page.orders.paymentRequired.banner", "Waiting for the user to add a card."),
    pending:      t("page.orders.transactionPending", "Payment is being processed."),
    failed:       t("page.orders.paymentStatus.failed", "Payment attempt failed."),
    refunded:     t("page.orders.paymentRequired.refundedNote", "Payment was refunded outside BagMails."),
    disputed:     t("page.orders.paymentRequired.disputedNote", "A chargeback dispute has been opened on this payment."),
  };
  return map[status] || "";
};

const PaymentSlot = ({ role, slotData, t, onRemind }) => {
  const [reminding, setReminding] = useState(false);

  const status = slotData?.status || "unpaid";
  const config = helpers.getPaymentStatusConfig(status);
  const fee = slotData?.platformFee;
  const feeCurrency = slotData?.transactionId?.currency || "eur";
  const feeSymbol = helpers.getCurrencySymbol(feeCurrency);
  const displayFee = fee && fee > 0 ? `${feeSymbol}${(fee / 100).toFixed(2)}` : "";
  const transaction = slotData?.transactionId;
  const userId = slotData?.userId;
  const userName = userId?.firstName
    ? `${userId.firstName} ${userId.lastName || ""}`.trim()
    : userId?.email || null;
  const isSender = role === "sender";
  const canRemind = ["unpaid", "pending_card", "failed"].includes(status);
  const showNote = ["unpaid", "pending_card", "failed", "refunded", "disputed"].includes(status);

  const handleRemind = async () => {
    setReminding(true);
    try {
      await onRemind(role);
    } finally {
      setReminding(false);
    }
  };

  return (
    <Card className="shadow border-0 overflow-hidden mb-3">
      <div className="p-3" style={{ background: config.bg, borderBottom: `2px solid ${config.border}` }}>
        <div className="d-flex flex-wrap justify-content-between align-items-start pd-gap-3">
          <div className="d-flex align-items-center pd-gap-3" style={{ minWidth: 0 }}>
            <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 square-34" style={{background: config.border }}>
              <i className={`ni ${isSender ? "ni-send" : "ni-delivery-fast"} text-sm`} style={{ color: config.text }} />
            </div>
            <div className="d-flex flex-column" style={{ minWidth: 0 }}>
              <p className="role-title" style={{color: config.text, wordBreak: "break-word", overflowWrap: "anywhere"}}>
                {isSender ? t("page.orders.sender", "Sender") : t("page.orders.traveler", "Traveler")}
              </p>
              {fee && fee > 0 ? (
                <p className="mb-0 text-11 opacity-8" style={{ color: config.text }}>
                  {t("page.orders.platformFee", "Platform Fee")}
                </p>
              ) : null}
            </div>
          </div>
          <div className="d-flex flex-column align-items-end pd-gap-2" style={{ minWidth: 0 }}>
            <div className="d-flex flex-wrap align-items-center justify-content-end pd-gap-2" style={{ minWidth: 0 }}>
              {fee && fee > 0 ? (
                <p className="mb-0 h3 font-weight-bold mr-2" style={{ color: config.text }}>
                  {displayFee}
                </p>
              ) : null}
              <span className="py-1 px-2 rounded-lg alert-link text-xs" style={{ color: config.text, background: config.border, wordBreak: "break-word", overflowWrap: "anywhere"}}>
                {helpers.getPaymentStatusLabel(status, t)}
              </span>
              {canRemind && (
                <button
                  onClick={handleRemind}
                  disabled={reminding}
                  title={t("page.orders.remind", "Remind")}
                  className="py-1 px-2 d-flex align-items-center pd-gap-1 remind-btn-base flex-shrink-0"
                  style={{
                    border: `1px solid ${config.border}`,
                    cursor: reminding ? "not-allowed" : "pointer",
                    color: config.text,
                    opacity: reminding ? 0.6 : 1,
                  }}
                >
                  <i className="ni ni-email-83" />
                  {reminding ? t("page.orders.reminding", "Sending...") : t("page.orders.remind", "Remind")}
                </button>
              )}
            </div>
            {showNote && (
              <p className="mb-0 text-11 opacity-9 text-right max-w-60" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                {getStatusNote(status, t)}
              </p>
            )}
          </div>
        </div>
      </div>
      <CardBody className="p-2">
        {userName && (
          <DetailRow label={t("page.orders.paidBy", "User")} value={userName} />
        )}
        {transaction && typeof transaction === "object" ? (
          <>
            {transaction.stripePaymentIntentId && (
              <CopyableRow
                label={t("page.orders.transactionId", "Transaction ID")}
                value={transaction.stripePaymentIntentId}
              />
            )}
            {transaction.userCardId?.last4 && (
              <DetailRow
                label={t("page.orders.cardUsed", "Card")}
                value={`${helpers.getCardBrandLabel(transaction.userCardId?.brand)} •••• ${transaction.userCardId.last4}`}
              />
            )}
            {transaction.paidAt && (
              <DetailRow
                label={t("page.orders.paidAt", "Paid At")}
                value={new Date(transaction.paidAt).toLocaleString()}
              />
            )}
            {transaction.refundedAt && (
              <DetailRow
                label={t("page.orders.refundedAt", "Refunded At")}
                value={new Date(transaction.refundedAt).toLocaleString()}
                danger
              />
            )}
            {transaction.disputeDeadline && (
              <DetailRow
                label={t("page.orders.disputeDeadline", "Dispute Deadline")}
                value={new Date(transaction.disputeDeadline).toLocaleString()}
                danger
              />
            )}
            {transaction.stripeDisputeId && (
              <CopyableRow
                label={t("page.orders.disputeId", "Dispute ID")}
                value={transaction.stripeDisputeId}
              />
            )}
            {transaction.failureReason && (
              <DetailRow
                label={t("page.orders.failureReason", "Failure Reason")}
                value={transaction.failureReason}
                danger
              />
            )}
            {transaction.stripeReceiptUrl && (
              <div className="pt-2">
                <a
                  href={transaction.stripeReceiptUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="pd-receipt-link"
                >
                  <i className="ni ni-external-link text-xs" />
                  {t("page.orders.viewReceipt", "View Stripe Receipt")}
                </a>
              </div>
            )}
          </>
        ) : (
          <div className="pd-empty-state-padding text-center">
            <i className="ni ni-credit-card d-block mb-2 text-slate-400 text-lg opacity-5"/>
            <p className="mb-0 text-13 text-slate-400 font-italic">
              {status === "unpaid" || status === "pending_card"
                ? t("page.orders.noPaymentYet", "No payment initiated yet")
                : t("page.orders.transactionPending", "Transaction details pending")}
            </p>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

const PaymentDetailsCard = ({ orderDetails }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const paymentDetails = orderDetails?.paymentDetails || {};
  const senderStatus = paymentDetails?.sender?.status || "unpaid";
  const travelerStatus = paymentDetails?.traveler?.status || "unpaid";
  const hasAnyPayment = senderStatus !== "unpaid" || travelerStatus !== "unpaid";
  const bothPaid = (senderStatus === "paid" || senderStatus === "succeeded") && (travelerStatus === "paid" || travelerStatus === "succeeded");
  const anyFailed = senderStatus === "failed" || travelerStatus === "failed";
  const anyDisputed = senderStatus === "disputed" || travelerStatus === "disputed";
  const anyRefunded = senderStatus === "refunded" || travelerStatus === "refunded";
  const handleRemind = async (role) => {
    try {
      const res = await userServices.sendPaymentReminder(orderDetails._id, role);
      const payload = res?.data || res;
      if (payload?.success) {
        notify("success", t(payload?.messageKey || "page.orders.remindSuccess", payload?.message || t("page.orders.remindSuccess", "Payment reminder sent successfully.")));
      } else {
        notify("error", t(payload?.messageKey || payload?.error || "page.orders.remindFailed", payload?.message || t("page.orders.remindFailed", "Failed to send reminder.")));
      }
    } catch (err) {
      const errPayload = err?.response?.data || {};
      notify("error", t(errPayload?.messageKey || errPayload?.error || "page.orders.remindFailed", errPayload?.message || t("page.orders.remindFailed", "Failed to send reminder.")));
    }
  };
  return (
    <Card className="shadow border-0 mb-3">
      <CardHeader className="bg-transparent border-b-2 pb-3">
        <div className="d-flex flex-wrap align-items-center justify-content-between pd-gap-3">
          <div className="d-flex align-items-center pd-gap-3" style={{ minWidth: 0 }}>
            <div className="d-flex align-items-center justify-content-center rounded bg-secondary square-34 flex-shrink-0">
              <i className="ni ni-credit-card text-primary text-m"/>
            </div>
            <div style={{ minWidth: 0 }}>
              <h3 className="mb-0 font-weight-bold heading-lg lh-120" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                {t("page.orders.paymentDetails", "Payment Details")}
              </h3>
              <p className="text-muted mb-0 text-xs mt-1" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                {helpers.interpolate(t("page.orders.platformFeeNote", "Platform fee: {{feePercent}} of order price + {{minimumFee}} minimum per party"), { feePercent: "5%", minimumFee: "€2.00" })}
              </p>
            </div>
          </div>
          <div className="d-flex flex-wrap justify-content-end pd-gap-2">
            {bothPaid && (
              <Badge color="success" className="text-xs px-2 py-1">
                <i className="ni ni-check-bold mr-1 text-xs"/>
                {t("page.orders.allSettled", "All Settled")}
              </Badge>
            )}
            {anyDisputed && (
              <Badge color="warning" className="text-xs px-2 py-1">
                <i className="ni ni-bell-55 mr-1 text-xs"/>
                {t("page.orders.paymentStatus.disputed", "Payment disputed")}
              </Badge>
            )}
            {anyRefunded && (
              <Badge color="info" className="text-xs px-2 py-1">
                <i className="ni ni-curved-next mr-1 text-xs"/>
                {t("page.orders.paymentStatus.refunded", "Refunded (outside BagMails)")}
              </Badge>
            )}
            {anyFailed && (
              <Badge color="danger" className="text-xs px-2 py-1">
                <i className="ni ni-fat-remove mr-1 text-xs" />
                {t("page.orders.actionNeeded", "Action Needed")}
              </Badge>
            )}
            {!hasAnyPayment && (
              <Badge color="secondary" className="text-xs px-2 py-1">
                {t("page.orders.noPaymentInitiated", "No payments initiated")}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardBody className="pt-2 pb-1">
        <PaymentSlot role="sender" slotData={paymentDetails?.sender || null} t={t} onRemind={handleRemind} />
        <PaymentSlot role="traveler" slotData={paymentDetails?.traveler || null} t={t} onRemind={handleRemind} />
      </CardBody>
    </Card>
  );
};

export default PaymentDetailsCard;